import {
  beginningOfMonthInZone,
  endOfMonthInZone,
  monthAnchorInZone,
} from '@modules/calendar-app/utils/display-zone-period.util';
import { type CalendarEvent as AngularCalendarEvent } from 'angular-calendar';

/** One month of the year view: the grid's `viewDate` anchor plus the events that fall in that month. */
export type MonthBucket<T> = [Date, AngularCalendarEvent<T>[]];

/**
 * Split `events` into the twelve months of `year`, reckoned in the display (account) zone.
 *
 * Both the anchor and the month interval MUST be display-zone values (SBUX-829 / PR #608): the mini
 * grids are laid out by the timezone-aware `DateAdapter` and the events arriving here are already
 * display-zone boundaries, so a browser-local `new Date(year, month, 1)` made every grid render the
 * PREVIOUS month and bucketed events near a month edge into the neighbouring month.
 *
 * An event belongs to a month when it starts in it, ends in it, or spans it entirely. Events with no
 * end are placed by their start alone.
 */
export function groupEventsByMonthInZone<T>(
  year: number,
  events: AngularCalendarEvent<T>[],
  timeZone: string | null | undefined,
): MonthBucket<T>[] {
  const buckets: MonthBucket<T>[] = [];

  for (let month = 0; month < 12; month++) {
    const monthStart = beginningOfMonthInZone(year, month, timeZone).getTime();
    const monthEnd = endOfMonthInZone(year, month, timeZone).getTime();

    const monthEvents = events.filter((event) => {
      const eventStart = event.start.getTime();
      const eventEnd = event.end?.getTime();
      return eventEnd
        ? (eventStart >= monthStart && eventStart <= monthEnd) ||
            (eventEnd >= monthStart && eventEnd <= monthEnd) ||
            (eventStart <= monthStart && eventEnd >= monthEnd)
        : eventStart <= monthEnd;
    });

    buckets.push([monthAnchorInZone(year, month, timeZone), monthEvents]);
  }

  return buckets;
}
